import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { impactStats, services, testimonials, campaigns } from '@/mocks/homeData';

function useCountUp(end: number, duration = 2000, start = 0) {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime: number | null = null;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isVisible, end, duration, start]);

  return { count, ref };
}

function StatItem({ label, value, icon }: { label: string; value: string; icon: string }) {
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''));
  const suffix = value.replace(/[0-9]/g, '');
  const { count, ref } = useCountUp(numericValue, 2500);

  return (
    <div ref={ref} className="text-center px-4 md:px-8">
      <div className="flex justify-center mb-3">
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-brand-50 flex items-center justify-center">
          <i className={`${icon} text-brand-600 text-xl md:text-2xl`}></i>
        </div>
      </div>
      <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-1">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-sm md:text-base text-gray-500 font-medium">{label}</div>
    </div>
  );
}

export default function Home() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [campaignHover, setCampaignHover] = useState<number | null>(null);

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const heroImage = 'https://readdy.ai/api/search-image?query=documentary%20style%20humanitarian%20aid%20workers%20distributing%20food%20and%20supplies%20to%20families%20in%20a%20warm%20community%20center%2C%20dramatic%20warm%20golden%20lighting%2C%20volunteers%20helping%20people%2C%20hope%20and%20compassion%2C%20professional%20photojournalism%20with%20warm%20orange%20brown%20earth%20tones%2C%20no%20blue%20or%20purple&width=1600&height=900&seq=hero-001&orientation=landscape';
  const aboutImage = 'https://readdy.ai/api/search-image?query=group%20of%20diverse%20volunteers%20smiling%20and%20working%20together%20at%20a%20community%20food%20drive%2C%20warm%20natural%20daylight%2C%20documentary%20photography%20style%2C%20authentic%20joyful%20expressions%2C%20teamwork%20and%20compassion%2C%20earth%20tones%20warm%20palette%2C%20no%20blue%20or%20purple&width=800&height=600&seq=about-001&orientation=landscape';
  const ctaImage = 'https://readdy.ai/api/search-image?query=silhouette%20of%20volunteers%20holding%20hands%20in%20circle%20at%20sunset%2C%20dramatic%20warm%20golden%20orange%20sky%2C%20humanitarian%20unity%20symbol%2C%20documentary%20photography%2C%20hope%20and%20togetherness%2C%20warm%20earth%20tones%2C%20no%20blue%20or%20purple&width=1600&height=900&seq=cta-001&orientation=landscape';

  return (
    <div className="overflow-x-hidden">
      {/* ===== HERO SECTION ===== */}
      <section className="relative w-full min-h-[100dvh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />

        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 py-32 md:py-0">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-12 lg:gap-8">
            {/* Left Content Area */}
            <div className="max-w-2xl">
              {/* Social Proof */}
              <div className="flex items-center gap-3 mb-8">
                <div className="flex -space-x-2.5">
                  <img
                    src="https://readdy.ai/api/search-image?query=close%20up%20portrait%20of%20a%20friendly%20male%20volunteer%20with%20warm%20smile%2C%20soft%20natural%20light%2C%20clean%20background%2C%20documentary%20portrait%20style%2C%20warm%20earth%20tones&width=100&height=100&seq=sp-001&orientation=squarish"
                    alt="Volunteer"
                    className="w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-white object-cover"
                  />
                  <img
                    src="https://readdy.ai/api/search-image?query=close%20up%20portrait%20of%20a%20friendly%20female%20volunteer%20with%20warm%20smile%2C%20soft%20natural%20light%2C%20clean%20background%2C%20documentary%20portrait%20style%2C%20warm%20earth%20tones&width=100&height=100&seq=sp-002&orientation=squarish"
                    alt="Volunteer"
                    className="w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-white object-cover"
                  />
                  <img
                    src="https://readdy.ai/api/search-image?query=close%20up%20portrait%20of%20a%20friendly%20older%20male%20volunteer%20with%20warm%20smile%2C%20soft%20natural%20light%2C%20clean%20background%2C%20documentary%20portrait%20style%2C%20warm%20earth%20tones&width=100&height=100&seq=sp-003&orientation=squarish"
                    alt="Volunteer"
                    className="w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-white object-cover"
                  />
                </div>
                <span className="text-white/80 text-sm md:text-base font-medium">
                  12,000+ volunteers worldwide
                </span>
              </div>

              <h1 className="text-white font-serif text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.05] mb-8">
                <span className="font-bold">Compassion</span>
                <br />
                <span className="font-light italic">in action</span>
              </h1>

              <Link
                to="/donate"
                className="inline-flex items-center gap-3 bg-white text-gray-900 px-8 py-4 rounded-full font-semibold text-base hover:bg-gray-50 transition-all duration-300 shadow-xl"
              >
                Make a Donation
                <i className="ri-arrow-right-line text-lg"></i>
              </Link>
            </div>

            {/* Right Content Area */}
            <div className="lg:max-w-md lg:text-right">
              <p className="text-white/80 text-base md:text-lg leading-relaxed">
                Since 2015, KELAB IKATAN BINA INSAN has been on the ground delivering food,
                education, disaster relief, and saving animals across 40 countries.
                Together with our donors and volunteers, we have touched over
                2.5 million lives.
              </p>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-px h-8 bg-white/30 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1/2 bg-white/70 animate-pulse" />
          </div>
        </div>
      </section>

      {/* ===== ABOUT SECTION ===== */}
      <section className="w-full py-20 md:py-28 lg:py-32 bg-warm-50">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            {/* Left Visual Area */}
            <div className="w-full lg:w-1/2">
              <div className="rounded-[32px] overflow-hidden shadow-2xl shadow-black/10 aspect-[4/3]">
                <img
                  src={aboutImage}
                  alt="Volunteers working together"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right Information Area */}
            <div className="w-full lg:w-1/2">
              <span className="inline-block bg-brand-100 text-brand-800 px-5 py-2 rounded-full text-sm font-semibold mb-6">
                About Our Mission
              </span>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                We believe in the
                <br />
                power of kindness
              </h2>

              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
                KELAB IKATAN BINA INSAN was born from a simple belief: every person
                deserves access to food, education, and safety. What started as a
                small community kitchen has grown into a global movement powered
                by thousands of compassionate donors and volunteers.
              </p>

              <Link
                to="/about"
                className="inline-flex items-center justify-between w-full md:w-auto md:min-w-[280px] bg-gray-100 hover:bg-gray-200 text-gray-900 px-7 py-4 rounded-2xl font-medium transition-all duration-300 group"
              >
                <span>Learn Our Story</span>
                <i className="ri-arrow-right-up-line text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== IMPACT STATS ===== */}
      <section className="w-full py-16 md:py-20 bg-white border-y border-gray-100">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-4">
            {impactStats.map((stat) => (
              <StatItem key={stat.label} {...stat} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES / FEATURES GRID ===== */}
      <section className="w-full py-20 md:py-28 lg:py-32 bg-warm-100">
        <div className="w-full px-6 md:px-10 lg:px-16">
          {/* Top Title Block */}
          <div className="mb-14 md:mb-20">
            <p className="text-gray-500 text-sm md:text-base font-light mb-3 tracking-wide">
              What We Do
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Our programs
              <br />
              change lives
            </h2>
          </div>

          {/* Bottom Card Area */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className="group bg-warm-50 rounded-2xl p-6 md:p-8 hover:bg-white hover:shadow-xl transition-all duration-500 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center mb-5 group-hover:bg-brand-500 transition-colors duration-300">
                  <i className={`${service.icon} text-brand-600 text-xl group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-500 text-sm md:text-base leading-relaxed font-light">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CAMPAIGNS GRID ===== */}
      <section className="w-full py-20 md:py-28 lg:py-32 bg-white">
        <div className="w-full px-6 md:px-10 lg:px-16">
          {/* Top Title Block */}
          <div className="mb-14 md:mb-20">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight">
              <span className="font-light">Active</span>
              <br />
              <span className="font-bold">campaigns</span>
            </h2>
          </div>

          {/* Card Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {campaigns.map((campaign) => {
              const progress = Math.round((campaign.raised / campaign.goal) * 100);
              const isHovered = campaignHover === campaign.id;

              return (
                <div
                  key={campaign.id}
                  className="group border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500"
                  onMouseEnter={() => setCampaignHover(campaign.id)}
                  onMouseLeave={() => setCampaignHover(null)}
                >
                  {/* Upper Image Zone */}
                  <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                    <img
                      src={campaign.image}
                      alt={campaign.title}
                      className={`w-full h-full object-cover transition-transform duration-700 ${isHovered ? 'scale-105' : 'scale-100'}`}
                    />
                    <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>

                  {/* Lower Content Zone */}
                  <div className="p-6 md:p-8">
                    <h3 className="font-serif text-xl md:text-2xl font-semibold text-gray-900 mb-3">
                      {campaign.title}
                    </h3>
                    <p className="text-gray-500 text-sm md:text-base mb-6 leading-relaxed">
                      {campaign.description}
                    </p>

                    {/* Progress */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-brand-600 font-semibold">
                          ${campaign.raised.toLocaleString()}
                        </span>
                        <span className="text-gray-400">
                          of ${campaign.goal.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-500 rounded-full transition-all duration-1000"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        {campaign.donors.toLocaleString()} donors
                      </span>
                      <Link
                        to="/donate"
                        className="text-sm font-semibold text-gray-900 hover:text-brand-600 transition-colors flex items-center gap-1"
                      >
                        Donate
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="w-full py-20 md:py-28 lg:py-32 bg-warm-50">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-4xl mx-auto">
            {/* Top Title */}
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 text-center mb-16">
              (Stories of hope)
            </h2>

            {/* Rating Badge */}
            <div className="flex items-center gap-2 mb-8">
              <div className="inline-flex items-center gap-1.5 bg-gray-900 text-white px-4 py-2 rounded-full text-sm font-medium">
                <i className="ri-star-fill text-brand-400"></i>
                <span>4.9</span>
              </div>
              <span className="text-gray-500 text-sm">from 3,200+ reviews</span>
            </div>

            {/* Review Body */}
            <div className="min-h-[200px]">
              <p className="font-serif text-xl md:text-2xl lg:text-3xl text-gray-800 leading-relaxed mb-10">
                {testimonials[activeTestimonial].text}
              </p>

              {/* User Info */}
              <div className="flex items-center gap-4">
                <img
                  src={testimonials[activeTestimonial].avatar}
                  alt={testimonials[activeTestimonial].name}
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-gray-900">
                    {testimonials[activeTestimonial].name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {testimonials[activeTestimonial].role}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-3 mt-10">
              <button
                onClick={prevTestimonial}
                className="w-12 h-12 rounded-xl border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                aria-label="Previous testimonial"
              >
                <i className="ri-arrow-left-line text-gray-700"></i>
              </button>
              <button
                onClick={nextTestimonial}
                className="w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center hover:bg-gray-800 transition-colors"
                aria-label="Next testimonial"
              >
                <i className="ri-arrow-right-line text-white"></i>
              </button>
              <div className="ml-auto flex gap-2">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTestimonial(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      idx === activeTestimonial ? 'bg-gray-900 w-8' : 'bg-gray-300'
                    }`}
                    aria-label={`Go to testimonial ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="relative w-full min-h-[80vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ctaImage})` }}
        />
        <div className="absolute inset-0 bg-black/50" />

        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 text-center">
          <h2 className="text-white text-4xl md:text-5xl lg:text-7xl font-bold uppercase tracking-wider mb-6">
            Join The Movement
          </h2>
          <p className="text-white/80 text-base md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Whether you donate, volunteer, or simply spread the word,
            <br className="hidden md:block" />
            you are part of the solution.
          </p>

          <Link
            to="/donate"
            className="inline-flex items-center gap-4 bg-gray-900 text-white pl-2 pr-7 py-2 rounded-full font-semibold text-base hover:bg-gray-800 transition-all duration-300 group"
          >
            <span className="w-10 h-10 rounded-full bg-brand-500 flex items-center justify-center group-hover:bg-brand-400 transition-colors">
              <i className="ri-arrow-right-up-line text-white text-lg"></i>
            </span>
            <span>Start Giving Today</span>
          </Link>
        </div>
      </section>
    </div>
  );
}